import React, { useState } from "react";
import StandardButton from "../Buttons/StandardButton";
import SiteMapImage from "../../resources/sitemap.jpg";
import { useNavigate } from "react-router-dom";
import { Job, Season } from "../../types";
import PulseLoader from "react-spinners/PulseLoader";
import { useQuery } from "@tanstack/react-query";
interface EditMapLinkProps {
  propertyId?: string;
  waitingForRender: boolean;
  currentThumbnailUrl?: string;
  disabled?: boolean;
  disabledClick?: () => Promise<void>;
  beforeNavigate?: () => Promise<BeforeNavigateResult>;
  season: Season;
  returnOnSave?: boolean;
  startInEditMode?: boolean;
}
type BeforeNavigateResult = { continue: boolean; propertyId?: string };

/**
 * A component to display a visual icon link to access the site map editing interface. It
 * allows actions to be called before navigating (i.e. saving data).  It also will render
 * the site map thumbnail and display a loading interface if it's not yet rendered.  It will
 * poll in the background if the thumbnail is not yet rendered.
 */
export const EditMapLink = ({
  propertyId,
  waitingForRender,
  currentThumbnailUrl,
  beforeNavigate,
  disabled = false,
  disabledClick,
  season,
  returnOnSave = false,
  startInEditMode = false,
}: EditMapLinkProps) => {
  const [loading, setLoading] = useState(false);
  const newJob = propertyId == null;
  const propertyQuery = useQuery({
    queryKey: ["job/" + propertyId],
    enabled: waitingForRender && !newJob,
    refetchInterval: (query) => {
      console.log(query.state.data);
      const jobData = query.state.data as Job | undefined;
      if (jobData && jobData[season]?.sitemapNeedsNewImage) {
        return query.state.dataUpdateCount > 10 ? 20000 : 1000; // backoff if it's still not generated after 10 fetches
      }
      return false;
    },
  });
  const jobInfo = propertyQuery.data as Job | undefined;
  // const internalWaitingForRender = false;
  const internalWaitingForRender =
    jobInfo?.[season]?.sitemapNeedsNewImage === true;
  const thumbnail = jobInfo?.[season]?.primarySitemap ?? currentThumbnailUrl;
  const navigate = useNavigate();

  const navigateToMap = async () => {
    setLoading(true);
    try {
      let result: BeforeNavigateResult = {
        continue: true,
        propertyId,
      };
      if (typeof beforeNavigate === "function") {
        result = await beforeNavigate();
      }
      if (result.continue) {
        const jobId = result.propertyId;

        const targetUrl = `/admin/property/${jobId}/site-map/${season}${startInEditMode ? "/edit" : ""}?${returnOnSave ? "return_to=job_update" : ""}`;

        navigate(targetUrl, { replace: false });
        // NOTE: setTimeout is required here due to a timing issue with React Router
        // where navigation immediately after async beforeNavigate callbacks can fail.
        // The async handleSaveJob() in beforeNavigate triggers state updates that
        // interfere with navigation timing. This ensures navigation happens after
        // the current execution context completes.
        // setTimeout(() => {
        //   navigate(targetUrl, { replace: false });
        // }, 0);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  return (
    <div
      className="relative w-[208px] h-[208px] overflow-hidden cursor-pointer"
      onClick={disabled ? disabledClick : navigateToMap}
    >
      <div className="absolute inset-0 z-10 grid grid-cols-1">
        <div className="place-self-center">
          {internalWaitingForRender ? (
            <>
              <PulseLoader
                color={"rgb(109, 104, 104)"}
                size={15}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="text-center "
              />
              <p style={{ color: "rgb(109, 104, 104)", fontWeight: "bold" }}>
                Generating Image
              </p>
            </>
          ) : (
            <StandardButton
              color="blue"
              loading={loading}
              className="inline-block hover:opacity-100"
              onClick={async (e: PointerEvent) => {
                e.stopPropagation(); // don't double trigger the event (div also has handler)
                e.preventDefault();
                await navigateToMap();
              }}
              disabled={disabled}
              disabledClick={disabledClick}
            >
              {thumbnail ? "Edit" : "Add"} Site Map
              {/* <br />
              Geo-Fencing */}
            </StandardButton>
          )}
        </div>
      </div>
      <img
        className={`absolute inset-0 ${thumbnail ? "" : "opacity-30"}`}
        src={thumbnail ?? SiteMapImage}
      />
    </div>
  );
};
